import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle, XCircle, ArrowsClockwise, DeviceMobile, Broom, Printer } from "@phosphor-icons/react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function StickerCard({ plate, kind }) {
    if (!plate) return null;
    const isGreen = kind === "green";
    const badgeCls = isGreen
        ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300"
        : "bg-red-500/10 border-red-500/40 text-red-300";
    const Icon = isGreen ? CheckCircle : XCircle;
    // Ask for a print-ready 1024×1024 PNG with high error correction so it
    // scans even when scaled up on paper.
    const qrUrl = `${API_BASE}/api/plates/${plate.id}/qr.png?size=1024&ec=H`;
    const printSticker = () => {
        const win = window.open("", "_blank", "width=800,height=1000");
        if (!win) return;
        win.document.write(`<!doctype html><html><head><title>Sticker ${plate.plate_number}</title>
            <style>
              @page { size: A4 portrait; margin: 18mm; }
              body { font-family: -apple-system, sans-serif; margin: 0; padding: 24px; color: #111; }
              .card { border: 2px solid #111; padding: 32px; max-width: 480px; margin: 0 auto; }
              .kicker { font-size: 10px; letter-spacing: 3px; text-transform: uppercase; color: #666; margin-bottom: 6px; }
              h1 { font-size: 32px; font-weight: 900; margin: 0 0 16px; letter-spacing: -0.5px; font-family: 'SF Mono', Menlo, monospace; }
              .qr-wrap { border: 1px solid #ddd; padding: 12px; background: #fff; text-align: center; margin: 20px 0; }
              .qr-wrap img { width: 100%; max-width: 380px; display: block; margin: 0 auto; image-rendering: pixelated; }
              .kid { font-family: 'SF Mono', Menlo, monospace; font-size: 10px; color: #888; text-align: center; letter-spacing: 1px; }
              table { width: 100%; border-collapse: collapse; margin-top: 16px; }
              td { padding: 6px 0; border-bottom: 1px solid #eee; font-size: 13px; }
              td.k { color: #888; text-transform: uppercase; font-size: 10px; letter-spacing: 2px; width: 45%; }
              td.v { font-family: 'SF Mono', Menlo, monospace; text-align: right; }
              .status { display: inline-block; padding: 4px 10px; font-size: 10px; letter-spacing: 3px; text-transform: uppercase; font-weight: 700;
                        background: ${isGreen ? "#0a7a3a" : "#c8102e"}; color: #fff; border-radius: 2px; }
              .foot { margin-top: 20px; font-size: 9px; color: #999; text-align: center; letter-spacing: 1.5px; text-transform: uppercase; }
            </style></head>
            <body>
              <div class="card">
                <div class="kicker">· UTAL MEA · WINDSHIELD STICKER</div>
                <h1>${plate.plate_number}</h1>
                <div class="status">${plate.status}</div>
                <div class="qr-wrap">
                  <img src="${qrUrl}" alt="QR"/>
                </div>
                <div class="kid">KID · ${plate.kid || ""}</div>
                <table>
                  <tr><td class="k">Owner</td><td class="v">${plate.owner_name || "—"}</td></tr>
                  <tr><td class="k">Make / Model</td><td class="v">${(plate.vehicle?.make || "") + " " + (plate.vehicle?.model || "")}</td></tr>
                  <tr><td class="k">Year</td><td class="v">${plate.vehicle?.year || "—"}</td></tr>
                  <tr><td class="k">VIN</td><td class="v">${plate.vehicle?.vin || "—"}</td></tr>
                  <tr><td class="k">Region</td><td class="v">${plate.vehicle?.governorate || "—"}</td></tr>
                  <tr><td class="k">Issued</td><td class="v">${plate.issued_at?.slice(0,10) || ""}</td></tr>
                  <tr><td class="k">Expires</td><td class="v">${plate.expires_at?.slice(0,10) || ""}</td></tr>
                  ${plate.revoked_reason ? `<tr><td class="k">Reason</td><td class="v" style="color:#c8102e">${plate.revoked_reason}</td></tr>` : ""}
                </table>
                <div class="foot">Ed25519 signed · Scannable by UTAL MEA verifier</div>
              </div>
              <script>window.onload = () => setTimeout(() => window.print(), 400);</script>
            </body></html>`);
        win.document.close();
    };
    return (
        <Card className="p-8 border-2" data-testid={`demo-sticker-${kind}`}>
            <div className="flex items-start justify-between mb-6">
                <div>
                    <div className="text-[10px] tracking-[0.35em] uppercase text-muted-foreground">UTAL MEA · WINDSHIELD STICKER</div>
                    <div className="mono text-3xl font-black mt-2 tracking-tight">{plate.plate_number}</div>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={printSticker} data-testid={`print-${kind}`} className="px-3 py-1.5 border border-primary/40 text-primary text-[10px] tracking-[0.3em] uppercase font-bold rounded-sm hover:bg-primary/10 transition-colors flex items-center gap-1.5">
                        <Printer size={14} weight="bold" /> Print
                    </button>
                    <div className={`px-3 py-1.5 border rounded-sm text-[10px] tracking-[0.3em] uppercase font-bold flex items-center gap-1.5 ${badgeCls}`}>
                        <Icon size={14} weight="fill" /> {plate.status}
                    </div>
                </div>
            </div>

            <div className="flex gap-8 items-start">
                <div className="flex-shrink-0">
                    <div className="bg-white p-4 rounded-sm border">
                        <img
                            src={qrUrl}
                            alt={`QR for ${plate.plate_number}`}
                            className="w-80 h-80 block"
                            style={{ imageRendering: "pixelated" }}
                            data-testid={`demo-qr-${kind}`}
                        />
                    </div>
                    <div className="text-[9px] mono text-muted-foreground mt-2 text-center break-all">KID: {plate.kid?.slice(-12)}</div>
                    <a href={qrUrl} download={`${plate.plate_number}-qr.png`} className="mt-2 block text-[10px] tracking-widest uppercase text-primary text-center hover:underline" data-testid={`download-${kind}`}>
                        Download PNG (1024px)
                    </a>
                </div>

                <div className="flex-1 space-y-3 text-sm">
                    <Field label="Owner" value={plate.owner_name} />
                    <Field label="Make / Model" value={`${plate.vehicle?.make || "—"} ${plate.vehicle?.model || ""}`} />
                    <Field label="Year" value={plate.vehicle?.year} />
                    <Field label="VIN" value={plate.vehicle?.vin} mono />
                    <Field label="Region" value={plate.vehicle?.governorate} />
                    <Field label="Color" value={plate.vehicle?.color} />
                    <Field label="Fuel" value={plate.vehicle?.fuel_type} />
                    <Field label="Issued" value={plate.issued_at?.slice(0, 10)} />
                    <Field label="Expires" value={plate.expires_at?.slice(0, 10)} />
                    {plate.revoked_at && <Field label="Revoked" value={plate.revoked_at?.slice(0, 10)} accent="red" />}
                    {plate.revoked_reason && <Field label="Reason" value={plate.revoked_reason} accent="red" />}
                </div>
            </div>
        </Card>
    );
}

function Field({ label, value, mono, accent }) {
    if (value === undefined || value === null || value === "") return null;
    return (
        <div className="grid grid-cols-[110px_1fr] gap-3">
            <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground pt-0.5">{label}</div>
            <div className={`${mono ? "mono" : ""} ${accent === "red" ? "text-red-400" : ""} text-sm`}>{String(value)}</div>
        </div>
    );
}

export default function DemoStickers() {
    const [green, setGreen] = useState(null);
    const [red, setRed] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/admin/demo/stickers");
            setGreen(r.data.green);
            setRed(r.data.red);
        } catch (e) {
            // Endpoint may 403 if not super_admin — no toast noise on demo landing.
        }
    };

    const seed = async () => {
        setBusy(true);
        try {
            const r = await api.post("/admin/demo/seed-stickers");
            setGreen(r.data.green);
            setRed(r.data.red);
            toast.success("Demo stickers ready");
        } catch (e) {
            toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Seed failed");
        } finally { setBusy(false); }
    };

    const reset = async () => {
        if (!window.confirm("Reset demo tenant?\n\nThis wipes ALL demo applications, plates, audit logs, scans, and CSV imports created by demo users, then re-issues the two demo stickers. Use this between prospects to hand out a clean slate.")) return;
        setBusy(true);
        try {
            const r = await api.post("/admin/demo/reset");
            setGreen(r.data.stickers?.green);
            setRed(r.data.stickers?.red);
            const s = r.data.removed || {};
            toast.success(`Demo reset · removed ${s.applications_removed}app / ${s.plates_removed}plate / ${s.audit_removed}audit / ${s.scans_removed}scan`);
        } catch (e) {
            toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Reset failed");
        } finally { setBusy(false); }
    };

    useEffect(() => { load(); }, []);

    const bothPresent = green && red;

    return (
        <div className="space-y-8" data-testid="demo-stickers-page">
            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div>
                    <div className="text-[11px] tracking-[0.35em] uppercase text-primary">· DEMO · FIELD-VERIFIABLE STICKERS</div>
                    <h1 className="text-4xl font-bold tracking-tight mt-2">Demo stickers</h1>
                    <p className="text-sm text-muted-foreground max-w-2xl mt-3">
                        Two live sticker records with real Ed25519-signed QR codes. Open this page on any screen, then scan with the
                        UTAL MEA mobile verifier — the active plate returns <span className="text-emerald-400 font-semibold">GREEN · VALID</span>,
                        the revoked plate returns <span className="text-red-400 font-semibold">RED · INVALID · revoked</span>.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button onClick={reset} disabled={busy} variant="outline" data-testid="demo-reset-btn" className="gap-2">
                        <Broom size={16} weight="bold" />
                        Reset demo tenant
                    </Button>
                    <Button onClick={seed} disabled={busy} data-testid="demo-seed-btn" className="gap-2">
                        <ArrowsClockwise size={16} weight="bold" />
                        {busy ? "Seeding…" : bothPresent ? "Re-seed" : "Seed demo stickers"}
                    </Button>
                </div>
            </div>

            {!bothPresent && (
                <Card className="p-8 text-center border-dashed">
                    <DeviceMobile size={40} weight="thin" className="mx-auto text-muted-foreground mb-3" />
                    <div className="text-sm text-muted-foreground max-w-lg mx-auto">
                        No demo stickers yet. Click <span className="text-foreground font-semibold">Seed demo stickers</span> above
                        to create <span className="mono">DEMO-GRN-001</span> (active) and <span className="mono">DEMO-RED-002</span> (revoked).
                        This is idempotent — click again anytime and existing records are kept.
                    </div>
                </Card>
            )}

            {green && <StickerCard plate={green} kind="green" />}
            {red && <StickerCard plate={red} kind="red" />}

            {bothPresent && (
                <Card className="p-6 border-dashed">
                    <div className="text-[10px] tracking-[0.35em] uppercase text-muted-foreground mb-3">How to demo</div>
                    <ol className="text-sm space-y-2 list-decimal ml-5">
                        <li>Open this page on your monitor/tablet (large screen for easy scanning).</li>
                        <li>Launch the UTAL MEA verifier app on your phone and log in as <span className="mono">officer@utalmea.demo</span>.</li>
                        <li>Point the camera at the <span className="text-emerald-400 font-semibold">DEMO-GRN-001</span> QR → phone shows <span className="text-emerald-400 font-semibold">VALID · GREEN</span> with owner + vehicle details.</li>
                        <li>Tap <span className="mono">Scan another</span>, then scan <span className="text-red-400 font-semibold">DEMO-RED-002</span> → phone shows <span className="text-red-400 font-semibold">INVALID · RED</span> with the revocation reason.</li>
                    </ol>
                </Card>
            )}
        </div>
    );
}
